/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {ChangeDetectionStrategy, Component, OnInit,} from '@angular/core';
import {NavigationEnd, ParamMap} from '@angular/router';
import {NbWindowService} from '@nebular/theme';
import {Catalog, CatalogWithAncestors, FileService, LazyCatalog} from 'compob2b-libs';
import {EMPTY, Observable} from 'rxjs';
import {filter, map, take, takeUntil} from 'rxjs/operators';
import {translations} from '../../../../../environments/translations.ru';
import {ListComponent} from '../../../../admin-shared/crud-components/list/list.component';
import {CrudConfig} from '../../../../admin-shared/interfaces/crud.config';
import {FormConfigService} from '../../../../admin-shared/services/form-config.service';
import {CATALOG_FORM} from '../catalog-forms.config';
import {CatalogService} from '../catalog.service';

@Component({
  selector: 'app-catalog-tree',
  templateUrl: './catalog-lazy.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogLazyComponent extends ListComponent<Catalog> implements OnInit {
  isOpened = true;
  catalogId = 0;
  catalog: LazyCatalog[];
  itemPage = false;
  config: CrudConfig = {
    uri: null,
    columns: CATALOG_FORM,
    defaultSortColumn: 'header',
    defaultSortDirection: 'asc',
    title: translations.catalog,
  };
  data1: LazyCatalog[] = [];

  constructor(protected readonly formsConfig: FormConfigService,
              protected readonly windowService: NbWindowService,
              protected readonly catalogService: CatalogService,
              protected readonly fileService: FileService
  ) {
    super(formsConfig, windowService, fileService);

    this.config.uri = this.catalogService.apiUrl;
  }

  get width(): number {
    let width = Number(localStorage.getItem('CatalogLazyWidth'));
    return width > 10 ? width : 310;
  }

  filterData(): Observable<Catalog[]> {
    return EMPTY;
  }

  initCatalogTree(): void {
    this.catalogService.initCatalogTree(this.catalogId, this.showDeleted)
      .pipe(
        take(1),
        map(tree => {
          const startTime = performance.now();

          if (!this.catalogId) {
            return tree;
          }

          const listOfRoot = tree.filter(item => item.parentId === 1);
          this.catalogService.treeRecursion(tree, listOfRoot);

          const endTime = performance.now();
          console.log(`Время выполнения initCatalogTree: ${endTime - startTime} мс`);
          return listOfRoot;
        })
      )
      .subscribe(result => {
        this.data1 = result;
      });
  }

  toggleOpened() {
    this.isOpened = !this.isOpened;
  }

  ngOnInit() {
    super.setConfig(this.config);
    super.ngOnInit();

    this.itemPage = this.router.url.indexOf('/item/') > -1;
    this.router.events.pipe(
      takeUntil(this.unsubscribe),
      filter(event => event instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      this.itemPage = event.url.indexOf('/item/') > -1;
    });

    this.route.queryParamMap
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(params => {
        this.loadByParams(params);
      });
  }

  loadByParams(params: ParamMap): void {
    this.catalogId = Number(params.get('catalog_id'));

    if (!this.catalogId) {
      this.data1.forEach(item => {
        const index = this.data1.indexOf(item);
        item.expanded = false;
        delete item.children;
        this.data1[index] = item;
      });

      this.initCatalogTree();
    }
    else {
      if (!this.catalogService.getParent(this.data1, this.catalogId)) {
        this.initCatalogTree();
      }
    }

    this.cdr.detectChanges();
  }

  toggleDeleted(): void {
    this.showDeleted = !this.showDeleted;
    this.initCatalogTree();
    this.cdr.detectChanges();
  }

  setId(itemId: number, navigate: boolean) {
    const item = this.catalogService.getParent(this.data1, itemId);
    this.catalogId = itemId;

    if (!item) {
      this.navigateToId(this.catalogId);

      return true;
    }
    item.expanded = !item.expanded;

    if (item.children === undefined && item.lastLevel === false) {
      this.catalogService.getLazyChildren(item.id, this.showDeleted)
        .pipe(take(1))
        .subscribe(dataChild => {
          this.catalogService.addChildrenToParentByParentId(item.id, item, dataChild);
          this.cdr.detectChanges();
        });
    }
    this.cdr.detectChanges();

    if (navigate) {
      this.navigateToId(this.catalogId);
    }
  }

  navigateToId(id: number) {
    if (!id) {
      this.router.navigate(['cabinet', 'pim', 'catalog']);
    }
    else {
      this.router.navigate(['cabinet', 'pim', 'catalog', id, 'products'], {queryParams: {catalog_id: this.catalogId}});
    }
  }

  afterDelete(id: number) {
    const catalogToDelete = this.catalogService.getParent(this.data1, id);
    if (catalogToDelete && catalogToDelete.parentId) {
      const parentCatalog = this.catalogService.getParent(this.data1, catalogToDelete.parentId);
      if (parentCatalog === null) {
        this.data1 = this.data1.filter(item => item.id !== id);
      }
      else {
        parentCatalog.children = parentCatalog.children.filter(item => item.id !== id);
      }

      this.successToast(translations.catalogRemoved);

      if (this.catalogId == id) {
        if (catalogToDelete.parentId && catalogToDelete.parentId !== 1) {
          this.navigateToId(catalogToDelete.parentId);
        }
        else {
          this.navigateToId(0);
        }
      }
    }

    this.cdr.detectChanges();
  }

  onCatalogSelected($event: CatalogWithAncestors) {
    this.setId($event.id, true);
  }

  // reindex() {
  //   if (this.reindexing) {
  //     return;
  //   }
  //   this.reindexing = true;
  //   this.loading = true;
  //   return this.compoApiService.get<any>('/catalog/reindex')
  //     .pipe(
  //       map(response => response.data),
  //       takeUntil(this.unsubscribe)
  //     ).subscribe(_ => {
  //       console.log('reindex');
  //       this.initCatalogTree();
  //       this.successToast('Процесс переиндексации каталогов завершён');
  //       this.reindexing = false;
  //       this.loading = false;
  //     });
  // }

  onResize(width: string) {
    localStorage.setItem('CatalogLazyWidth', parseInt(width).toString());
  }

}
